import { z } from 'zod';
import { centavos } from './envelope.ts';

/**
 * §5 Affiliate management — the request shapes for every write in the module.
 *
 * Its own file rather than more of `lists.ts`, for the same reason the module
 * has its own route file: the affiliate domain is structurally independent of
 * the agent one — no downline, no override, three commission models, a click
 * funnel instead of a hierarchy — and folding its contracts into the shared
 * list file is how two domains start borrowing each other's assumptions.
 *
 * Every reason below is a CLOSED SET, not free text. A reason code drives the
 * audit row a regulator reads and the report that counts decisions by cause,
 * and neither can group on a sentence somebody typed.
 */

/* ── Commission models ───────────────────────────────────────────────────── */

/**
 * The three models `affiliate.affiliate_plan` allows, and the CHECK behind it
 * (`affiliate_plan_model_coherent`) decides which fields each one must carry:
 * REVSHARE needs a rate, CPA needs an amount, HYBRID needs both. The refinement
 * below is that constraint restated where the operator can still fix it.
 */
export const affiliateModel = z.enum(['REVSHARE', 'CPA', 'HYBRID']);
export type AffiliateModel = z.infer<typeof affiliateModel>;

const planTerms = {
  model: affiliateModel,
  /** Share of net GGR in basis points. 30% is 3000. */
  revshareBp: z.number().int().min(0).max(10000).nullable().optional(),
  /** One-time bounty per qualifying first deposit, in centavos. */
  cpaAmount: centavos.nullable().optional(),
  /** A CPA award is held this long before it becomes payable. */
  cpaHoldDays: z.number().int().min(0).max(365).default(30),
  /** The qualifying floor: a first deposit below this earns no bounty. */
  cpaMinDeposit: centavos.nullable().optional(),
};

/** The shape `coherent` needs to see. Every schema it wraps has these three. */
interface PlanTermsShape {
  model?: string;
  revshareBp?: number | null | undefined;
  cpaAmount?: string | null | undefined;
}

/**
 * The model's own coherence, checked BEFORE the database sees it.
 *
 * `affiliate_plan_model_coherent` would refuse an incoherent row anyway, and
 * that constraint is what actually holds. This exists so the operator is told
 * WHICH field is missing, inline, rather than being handed "that change is not
 * allowed by the platform rules" for a form they can still correct.
 */
function coherent<T extends z.ZodType<PlanTermsShape>>(schema: T) {
  return schema.superRefine((value, ctx) => {
    const needsRate = value.model === 'REVSHARE' || value.model === 'HYBRID';
    const needsAmount = value.model === 'CPA' || value.model === 'HYBRID';
    if (needsRate && (value.revshareBp === null || value.revshareBp === undefined)) {
      ctx.addIssue({
        code: 'custom',
        path: ['revshareBp'],
        message: 'A revenue-share plan needs a rate.',
      });
    }
    if (needsAmount && (value.cpaAmount === null || value.cpaAmount === undefined)) {
      ctx.addIssue({
        code: 'custom',
        path: ['cpaAmount'],
        message: 'A CPA plan needs a bounty amount.',
      });
    }
    if (needsAmount && value.cpaAmount !== null && value.cpaAmount !== undefined) {
      if (BigInt(value.cpaAmount) <= 0n) {
        ctx.addIssue({
          code: 'custom',
          path: ['cpaAmount'],
          message: 'A CPA bounty has to be more than nothing.',
        });
      }
    }
  });
}

/**
 * Why a commission model is being changed.
 *
 * A plan change is dated and never retroactive — the players already
 * attributed keep the plan they were acquired under — so the code records why
 * the NEXT cohort is on different terms.
 */
export const affiliatePlanChangeReason = z.enum([
  'NEW_AGREEMENT',
  'RENEGOTIATED',
  'PERFORMANCE_UPLIFT',
  'PERFORMANCE_DOWNGRADE',
  'MODEL_SWITCH',
  'CORRECTION',
]);

export const setAffiliatePlanRequest = coherent(
  z.object({
    ...planTerms,
    reasonCode: affiliatePlanChangeReason,
    note: z.string().min(3, 'Say why the terms are changing.').max(500),
  }),
);
export type SetAffiliatePlanRequest = z.infer<typeof setAffiliatePlanRequest>;

/* ── Creating an affiliate ───────────────────────────────────────────────── */

/**
 * The traffic sources the network actually recruits through.
 *
 * A closed set because `source_kind` is the column the whole screen filters,
 * searches and groups on — free text there means "TikTok", "Tiktok" and
 * "tik tok" are three sources, and the quality review that compares them is
 * comparing nothing.
 */
export const affiliateSourceKind = z.enum([
  'SEO / review site',
  'SEO / blog',
  'Twitch / YouTube',
  'TikTok',
  'Facebook community',
  'Telegram channel',
  'Affiliate network',
  'Paid search',
]);

export const createAffiliateRequest = coherent(
  z.object({
    name: z.string().min(2, 'Give the affiliate a name.').max(200),
    brandId: z.string().min(1, 'Pick the brand this affiliate sends traffic to.'),
    sourceKind: affiliateSourceKind,
    /**
     * The sub-ID the first tracking link is issued under. "A tracking link and
     * sub-ID prefix are issued on creation", so one is written with the
     * affiliate rather than left for somebody to remember.
     */
    subId: z
      .string()
      .min(3, 'A sub-ID needs at least three characters.')
      .max(48)
      .regex(
        /^[a-z0-9_]+$/,
        'A sub-ID is lowercase letters, digits and underscores — it goes in a URL.',
      )
      .default('sub_home'),
    channel: z.string().min(2, 'Name the channel this link is for.').max(120).default('Site homepage'),
    ...planTerms,
  }),
);
export type CreateAffiliateRequest = z.infer<typeof createAffiliateRequest>;

/* ── Approving, and closing a quality review ─────────────────────────────── */

export const approveAffiliateRequest = z.object({
  note: z.string().min(3, 'Record why this affiliate is being approved.').max(500),
});
export type ApproveAffiliateRequest = z.infer<typeof approveAffiliateRequest>;

/**
 * How a quality review ended.
 *
 * The worker OPENS a review when FTD conversion falls under the configured
 * floor; only a person closes one, and the outcome is what the trail has to
 * carry. CLEARED puts the affiliate back to ACTIVE; SUSPENDED stops new
 * attribution and keeps existing players earning revshare, which is what the
 * module rule says suspension does.
 */
export const qualityReviewOutcome = z.enum(['CLEARED', 'SUSPENDED']);

export const closeQualityReviewRequest = z.object({
  outcome: qualityReviewOutcome,
  note: z.string().min(3, 'Record what the review found.').max(500),
});
export type CloseQualityReviewRequest = z.infer<typeof closeQualityReviewRequest>;

/* ── The affiliate portal's own writes ───────────────────────────────────── */

/**
 * An early payout, requested BY the affiliate.
 *
 * The affiliate is the maker and can never be the checker:
 * `affiliate_payout_maker_is_not_checker` is a real CHECK, and the row is
 * inserted with `approved_by` null so that a person in the back office has to
 * release it. Nothing here lets a partner change its own commission terms —
 * the amount is capped at what it is already owed.
 */
export const earlyPayoutRequest = z.object({
  amount: centavos,
  note: z.string().max(500).optional(),
});
export type EarlyPayoutRequest = z.infer<typeof earlyPayoutRequest>;

/**
 * Topics an affiliate can raise a ticket under.
 *
 * `support.ticket.topic` is a free-text column with no enum behind it, which is
 * why this list is the enum: the back office's support queue groups by topic,
 * and a partner typing its own would make every group a group of one.
 */
export const affiliateTicketTopic = z.enum([
  'Payout question',
  'Traffic or link issue',
  'Player complaint',
  'Request a game',
  'Request a promotion',
  'Something else',
]);

export const createAffiliateTicketRequest = z.object({
  topic: affiliateTicketTopic,
  subject: z.string().min(3, 'Give the ticket a subject.').max(300),
  body: z.string().min(3, 'Say what the platform team needs to know.').max(4000),
});
export type CreateAffiliateTicketRequest = z.infer<typeof createAffiliateTicketRequest>;

export const affiliateTicketReplyRequest = z.object({
  body: z.string().min(1, 'Write a reply.').max(4000),
});
export type AffiliateTicketReplyRequest = z.infer<typeof affiliateTicketReplyRequest>;
